import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useMutation } from '@apollo/client';
import { modifyCommissionSplit } from '@common/reusable/commission-split/queries';
import { IModifyCommissionSplitVars } from '@common/reusable/commission-split/types';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getPolicyBusinessPartners } from '@features/production/policy';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';
import { isValidNumber } from '@utils/validationUtils';

import { isEmpty } from 'lodash';
import PrecisionService from 'src/core/services/PrecisionService';
const initialValues = {
  primaryBP: '',
  primaryPercentage: 0,
  secondaryBP: '',
  secondaryPercentage: 0,
  additionalBP: '',
  additionalPercentage: 0
};
const drawerSections = (
  data: IUpdateCommissionProposalData,
  lovs: Record<string, string>
): IDynamicFormWidgetSection[] => {
  return [
    {
      inputs: {
        primaryBP: {
          name: 'primaryBP',
          title: 'Primary Business Partner',
          type: FormFieldType.select,
          placeholder: 'Primary Business Partner',
          value: data?.primaryBP,
          required: true,
          disabled: true,
          selectOptions: lovs
        },
        primaryPercentage: {
          name: 'primaryPercentage',
          title: 'Share',
          type: FormFieldType.percentage,
          placeholder: 'Share',
          value: PrecisionService.multiplyBy100(
            data?.primaryPercentage ?? initialValues.primaryPercentage
          ),
          required: true
        },
        secondaryBP: {
          name: 'secondaryBP',
          title: 'Secondary Business Partner',
          type: FormFieldType.select,
          placeholder: 'Secondary Business Partner',
          value: data?.secondaryBP,
          required: !isEmpty(data?.secondaryBP),
          hidden: isEmpty(data?.secondaryBP),
          disabled: true,
          selectOptions: lovs
        },
        secondaryPercentage: {
          name: 'secondaryPercentage',
          title: 'Share',
          type: FormFieldType.percentage,
          placeholder: 'Share',
          value: PrecisionService.multiplyBy100(
            data?.secondaryPercentage ?? initialValues.secondaryPercentage
          ),
          required: !isEmpty(data?.secondaryBP),
          hidden: isEmpty(data?.secondaryBP)
        },
        additionalBP: {
          name: 'additionalBP',
          title: 'Additional Business Partner',
          type: FormFieldType.select,
          placeholder: 'Additional Business Partner',
          value: data?.additionalBP || initialValues.additionalBP,
          required: !isEmpty(data?.additionalBP),
          hidden: isEmpty(data?.additionalBP),
          disabled: true,
          selectOptions: lovs
        },
        additionalPercentage: {
          name: 'additionalPercentage',
          title: 'Share',
          type: FormFieldType.percentage,
          placeholder: 'Share',
          value: PrecisionService.multiplyBy100(
            data?.additionalPercentage ?? initialValues.additionalPercentage
          ),
          required: !isEmpty(data?.additionalBP),
          hidden: isEmpty(data?.additionalBP)
        }
      }
    }
  ];
};

export interface IUpdateCommissionProposalData {
  primaryBP: string;
  primaryPercentage: number;
  secondaryBP: string;
  secondaryPercentage: number;
  additionalBP: string;
  additionalPercentage: number;
}

type Props = {
  id: string;
  open?: boolean;
  onClose: () => void;
  data: IUpdateCommissionProposalData;
  partnerLovs: Record<string, string>;
};
const UpdateCommissionSplitDrawer: React.FC<Props> = ({
  id,
  open,
  onClose,
  data,
  partnerLovs
}) => {
  const [booted, setBooted] = useState<boolean>(false);

  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>();

  const [updateCommissionAction] = useMutation<IModifyCommissionSplitVars>(
    modifyCommissionSplit(),
    { refetchQueries: [getPolicyBusinessPartners] }
  );

  const initialize = () => {
    const initialData = drawerSections(data, partnerLovs);
    setInputsForm(initialData);
    setBooted(true);
  };

  useEffect(() => {
    if (open) {
      initialize();
    } else {
      setInputsForm(undefined);
    }
  }, [open]);

  const onCustomBlur = async (
    fieldName: string,
    values: Record<string, any>,
    errors: Record<string, string>
  ) => {
    const totalShare =
      Number(values.primaryPercentage || 0) +
      Number(values.secondaryPercentage || 0) +
      Number(values.additionalPercentage || 0);

    const errorMessage = 'Total Share should be equal to 100%';

    if (totalShare !== 100) {
      errors.primaryPercentage = errorMessage;
      errors.secondaryPercentage = errorMessage;
      errors.additionalPercentage = errorMessage;
    } else {
      delete errors.primaryPercentage;
      delete errors.secondaryPercentage;
      delete errors.additionalPercentage;
    }

    return { values, errors };
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(inputsForm, values);
    setSubmitButtonState('loading');
    setFormDisabled(true);

    const buildPartner = (
      id: string,
      type: 'PRIMARY' | 'SECONDARY' | 'ADDITIONAL',
      share: any
    ) => ({
      policyBusinessPartnerID: id,
      policyBusinessPartnerType: type,
      sharePercentage: isEmpty(id)
        ? 0
        : isValidNumber(share)
          ? PrecisionService.divideBy100(share)
          : 0
    });

    const policyBusinessPartners = [
      data.primaryBP &&
        data.primaryPercentage &&
        buildPartner(data.primaryBP, 'PRIMARY', data.primaryPercentage),
      data.secondaryBP &&
        data.secondaryPercentage &&
        buildPartner(data.secondaryBP, 'SECONDARY', data.secondaryPercentage),
      data.additionalBP &&
        data.additionalPercentage &&
        buildPartner(data.additionalBP, 'ADDITIONAL', data.additionalPercentage)
    ].filter(Boolean);

    const variables = {
      id,
      input: {
        policyBusinessPartners
      }
    };

    try {
      const res = await updateCommissionAction({ variables });

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {'Commission Split updated successfully!'}
          </ToastSuccessMessage>
        );
        setSubmitButtonState('success');
        // onSuccess();
        onClose();
      } else {
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  if (!inputsForm) {
    return <></>;
  }

  return (
    <DynamicFormDrawer
      title="Modify Commission Split"
      onClose={() => onClose()}
      onCustomBlur={onCustomBlur}
      isOpen={open}
      loading={!booted}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export { UpdateCommissionSplitDrawer };
